import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Avatar, Flex, Icon, UiButton, UiText } from '@shared/ui';
import { getEstimateInformationQuery } from '@widgets/EstimateInformation/api/hooks/getEstimateInformationQuery';
import { useRemoveEstimateUserQuery } from '@widgets/EstimateInformation/api/hooks/useRemoveEstimateUserQuery';
import { IEmployee } from '@widgets/EstimateInformation/model/types/types';

import cls from './EmployeeCard.module.css';

interface EmployeeCardProps {
    classes?: string;
    employee: IEmployee;
    mode: 'managers' | 'purchasers' | null;
    employeesLength: number;
    addingUserEmail: string | null;
}

export const EmployeeCard = ({
    classes,
    employee,
    mode,
    employeesLength,
    addingUserEmail,
}: EmployeeCardProps) => {
    const { t } = useTranslation();
    const { refetch, data } = getEstimateInformationQuery();
    const removeEstimateUserMutation = useRemoveEstimateUserQuery();

    const roleMapping = {
        managers: 'manager',
        purchasers: 'purchase_agent',
    };

    const isEditMode =
        (mode === 'managers' && data?.managersData.canAdd) ||
        (mode === 'purchasers' && data?.purchasersData.canAdd);

    const isHideDeleteButton = mode === 'managers' && employeesLength === 1;

    const onRemoveEmployee = async () => {
        const formData = new FormData();

        formData.append('role', roleMapping[mode]);
        formData.append('email', employee.email);

        const response = await removeEstimateUserMutation.mutateAsync(formData);

        if (response?.data?.data?.status === 'redirect') {
            window.location.href = response.data.data.data.redirect;
            return;
        }
        await refetch();
    };
    return (
        <Flex
            justify="between"
            align="center"
            className={cn(cls.EmployeesCard, addingUserEmail === employee.email && cls.adding, [
                classes,
            ])}
        >
            <Flex justify="start" align="center" gap={8}>
                <Avatar width={32} height={32} avatarUrl={employee.avatarUrl} />
                <Flex direction="column" align="start">
                    {employee.name && (
                        <UiText variant="mediumPlain" className={cls.name}>
                            {employee.name}
                        </UiText>
                    )}
                    <UiText variant="mediumPlain" className={!employee.name ? cls.name : cls.email}>
                        {employee.email}
                    </UiText>
                    {employee.isPending && (
                        <UiText variant="mediumPlain" className={cls.pending}>
                            {t('EmployeeCard.return.EmployeesCard.Flex.UiText.pending')}
                        </UiText>
                    )}
                </Flex>
            </Flex>
            {!isHideDeleteButton && isEditMode && (
                <UiButton
                    onClick={onRemoveEmployee}
                    className={cls.delete}
                    variant="icon"
                    startIcon={<Icon iconName="filesDelete" fill="#818998" />}
                />
            )}
        </Flex>
    );
};
